import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
import DomainHelper from "helpers/DomainHelper";
import {
  AddKeywordsPayload,
  AddKeywordsResponse,
  KeywordsError,
  SocialScrapingFilterOptions,
  SocialScrapingState,
  SocialScrapingKeyword
} from "types/scraping/socialScraping.type";
import { RootState } from "store/store";
import { ScrapedComment } from "types/scraping/socialScrapingComments.type";

const baseUrl = DomainHelper.getAPIMain();

export const getScrapedContent = createAsyncThunk(
  "socialScraping/getContent",
  async ({ options = {} }: { options?: SocialScrapingFilterOptions }, thunkAPI) => {
    try {
      const params = new URLSearchParams();
      if (options.page) params.append('page', options.page.toString());
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.platform) params.append('platform', options.platform);
      if (options.sentiment) params.append('sentiment', options.sentiment);
      if (options.keyword) params.append('keyword', options.keyword);
      if (options.clientId) params.append('clientId', options.clientId);
      const url = `${baseUrl}scraping/content${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axios.get(url, { withCredentials: true });
      return thunkAPI.fulfillWithValue(response.data);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: "Error fetching content" });
    }
  }
);
export const getScrapedComments = createAsyncThunk(
  "socialScraping/getComments",
  async ({ postId }: { postId: string }, thunkAPI) => {
    try {
      const response = await axios.get<{ data: ScrapedComment[] }>(`${baseUrl}scraping/comments?postId=${postId}`, { withCredentials: true });
      return thunkAPI.fulfillWithValue(response.data);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: "Error fetching content" });
    }
  }
);
export const initialScrape = createAsyncThunk(
  "socialScraping/initialScrape",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${baseUrl}testing/run_scraping`,
        { withCredentials: true }
      );
      return thunkAPI.fulfillWithValue(response.data);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: "Error starting scraping" });
    }
  }
);
export const getKeywords = createAsyncThunk(
  "socialScraping/getKeywords",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${baseUrl}scraping/keywords`,
        { withCredentials: true }
      );
      return thunkAPI.fulfillWithValue(response.data);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: "Error fetching keywords" });
    }
  }
);
export const addKeywords = createAsyncThunk<
  AddKeywordsResponse,
  AddKeywordsPayload,
  { rejectValue: KeywordsError }
>(
  "socialScraping/addKeywords",
  async ({ keywords }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${baseUrl}scraping/keywords`,
        { keywords },
        { withCredentials: true }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data || {
          message: "Error adding keywords",
          success: false
        }
      );
    }
  }
);
export const removeKeyword = createAsyncThunk(
  "socialScraping/removeKeyword",
  async ({ keywordId }: { keywordId: string }, thunkAPI) => {
    try {
      await axios.delete(
        `${baseUrl}scraping/keyword/delete?keywordId=${keywordId}`,
        { withCredentials: true }
      );
      return { keywordId };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data || {
          message: "Error removing keyword",
          success: false
        }
      );
    }
  }
);
export const updateKeyword = createAsyncThunk(
  "socialScraping/updateKeyword",
  async ({ keywordId, keyword, clientId }: { keywordId: string, keyword: string, clientId: string }, thunkAPI) => {
    try {
      // Adicionado clientId como query parameter
      const response = await axios.put(
        `${baseUrl}scraping/keyword/update/${keywordId}?clientId=${clientId}`,
        { keyword },
        { withCredentials: true }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data || {
          message: "Error updating keyword",
          success: false
        }
      );
    }
  }
);

const initialState: SocialScrapingState = {
  content: {
    data: [],
    total: 0,
    page: 1,
    limit: 20,
    isLoading: false,
    isSuccess: false,
    errorMessage: null,
  },
  keywords: {
    data: [],
    isLoading: false,
    isSuccess: false,
    errorMessage: null,
  },
  scraping: {
    isLoading: false,
    isSuccess: false,
    errorMessage: null,
    successMessage: null,
    count: null,
  },
};
const socialScrapingSlice = createSlice({
  name: "socialScraping",
  initialState,
  reducers: {
    setContentPage: (state, action) => {
      state.content.page = action.payload;
    },
    setContentLimit: (state, action) => {
      state.content.limit = action.payload;
    },
    resetScrapingState: (state) => {
      state.scraping.isLoading = false;
      state.scraping.isSuccess = false;
      state.scraping.errorMessage = null;
      state.scraping.successMessage = null;
      state.scraping.count = null;
    }
  },
  extraReducers: (builder) => {
    // getScrapedContent
    builder
      .addCase(getScrapedContent.pending, (state) => {
        state.content.isLoading = true;
      })
      .addCase(getScrapedContent.fulfilled, (state, action) => {
        state.content.isLoading = false;
        state.content.isSuccess = true;
        state.content.data = action.payload.data.content;
        state.content.total = action.payload.data.total;
        state.content.page = action.payload.data.page;
        state.content.limit = action.payload.data.limit;
      })
      .addCase(getScrapedContent.rejected, (state, action) => {
        state.content.isLoading = false;
        state.content.isSuccess = false;
        state.content.errorMessage = action.error.message || "Error fetching content";
      });

    // initialScrape
    builder
      .addCase(initialScrape.pending, (state) => {
        state.scraping.isLoading = true;
      })
      .addCase(initialScrape.fulfilled, (state, action) => {
        state.scraping.isLoading = true;
      })
      .addCase(initialScrape.rejected, (state, action) => {
        state.scraping.isLoading = false;
      });

    builder
      .addCase(getKeywords.pending, (state) => {
        state.keywords.isLoading = true;
      })
      .addCase(getKeywords.fulfilled, (state, action) => {
        state.keywords.isLoading = false;
        state.keywords.isSuccess = true;
        state.keywords.data = action.payload.data;
      })
      .addCase(getKeywords.rejected, (state, action) => {
        state.keywords.isLoading = false;
        state.keywords.isSuccess = false;
        state.keywords.errorMessage = action.error.message || "Error fetching keywords";
      });

    builder
      .addCase(addKeywords.pending, (state) => {
        state.keywords.isLoading = true;
      })
      .addCase(addKeywords.fulfilled, (state, action) => {
        state.keywords.isLoading = false;
        state.keywords.isSuccess = true;
        if (Array.isArray(action.payload.keywords)) {
          state.keywords.data = [
            ...state.keywords.data,
            ...action.payload.keywords
          ];
        }
      })
      .addCase(addKeywords.rejected, (state, action) => {
        state.keywords.isLoading = false;
        state.keywords.isSuccess = false;
        state.keywords.errorMessage = action.payload?.message || "Error adding keywords";
      });

    // removeKeyword
    builder
      .addCase(removeKeyword.pending, (state) => {
        state.keywords.isLoading = true;
      })
      .addCase(removeKeyword.fulfilled, (state, action) => {
        state.keywords.isLoading = false;
        state.keywords.isSuccess = true;
        state.keywords.data = state.keywords.data.filter(
          keyword => keyword.id !== action.payload.keywordId
        );
      })
      .addCase(removeKeyword.rejected, (state, action) => {
        state.keywords.isLoading = false;
        state.keywords.isSuccess = false;
        state.keywords.errorMessage = action.error?.message || "Error removing keyword";
      });

    // updateKeyword
    builder
      .addCase(updateKeyword.pending, (state) => {
        state.keywords.isLoading = true;
      })
      .addCase(updateKeyword.fulfilled, (state, action) => {
        state.keywords.isLoading = false;
        state.keywords.isSuccess = true;
        const updatedKeywordIndex = state.keywords.data.findIndex(
          keyword => keyword.id === action.payload.data.id
        );
        if (updatedKeywordIndex !== -1) {
          state.keywords.data[updatedKeywordIndex] = action.payload.data;
        }
      })
      .addCase(updateKeyword.rejected, (state, action) => {
        state.keywords.isLoading = false;
        state.keywords.isSuccess = false;
        state.keywords.errorMessage = action.error?.message || "Error updating keyword";
      });
  }
});

export const {
  setContentPage,
  setContentLimit,
  resetScrapingState,
} = socialScrapingSlice.actions;

export const selectScrapingContent = (state: RootState) => state.socialScraping.content.data;
export const selectContentIsLoading = (state: RootState) => state.socialScraping.content.isLoading;
export const selectContentTotal = (state: RootState) => state.socialScraping.content.total;
export const selectContentPage = (state: RootState) => state.socialScraping.content.page;
export const selectContentLimit = (state: RootState) => state.socialScraping.content.limit;
export const selectKeywords = (state: RootState) => state.socialScraping.keywords.data;
export const selectKeywordsIsLoading = (state: RootState) => state.socialScraping.keywords.isLoading;
export const selectScrapingStatus = (state: RootState) => state.socialScraping.scraping;

export default socialScrapingSlice;